import { getItemFromLocalStorage, removeItemFromSessionStorage } from '@utils/web-storage';
import accountSecurityRoutes from './account-security';
import { addFunds } from '@store/modules/add-funds';
import addFundsRoutes from './add-funds';
import { app } from '@src/store/modules/app';
import { autoInvest } from '@store/modules/auto-invest';
import autoInvestEnableRoutes from './auto-invest-enable';
import { investmentEntity } from '@store/modules/investment-entity';
import { ira } from '@store/modules/ira';
import { orderForm } from '@store/modules/order-form';
import { questionnaire } from '@store/modules/questionnaire';
import riaUpgradeRoutes from './ria-upgrade';
import { RouteConfig } from 'vue-router';
import { tax } from '@store/modules/tax';
import upgradeRoutes from './upgrade';
import { user } from '@store/modules/user';

/* eslint-disable @typescript-eslint/explicit-function-return-type */
const accountRoutes: Array<RouteConfig> = [
	{
		path: `/account`,
		name: 'account-subpage',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			investmentEntity.storePositions();
			next();
		},
		component: () => import(/* webpackChunkName: "account-subpage" */ '@views/account/account-subpage.vue'),
		redirect: { name: 'account-overview' },
		children: [
			{
				path: 'documents',
				name: 'account-documents',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					if (investmentEntity.showTaxCenter) {
						tax.storeTaxData();
					}
					next();
				},
				component: () =>
					import(/* webpackChunkName: "account-documents" */ '@views/account/account-documents.vue')
			},
			{
				path: 'portfolio',
				name: 'account-portfolio',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					investmentEntity.storePositions();
					next();
				},
				component: () =>
					import(/* webpackChunkName: "account-portfolio" */ '@views/account/account-portfolio.vue')
			},
			{
				path: 'portfolio/reit/:reitId',
				name: 'account-portfolio-detail',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					const isId = /^\d+$/.test(to.params.reitId);
					if (isId) {
						next();
					} else {
						next({ name: 'account-portfolio' });
					}
				},
				component: () =>
					import(
						/* webpackChunkName: "account-portfolio-reit-ipo-detail" */ '@views/account/account-portfolio-reit-ipo-detail.vue'
					)
			},
			{
				path: 'portfolio/note-investment/:noteId',
				name: 'account-portfolio-notes-detail',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				component: () =>
					import(
						/* webpackChunkName: "account-portfolio-note-detail" */ '@views/account/account-portfolio-note-detail.vue'
					)
			},
			{
				path: 'performance',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					await investmentEntity.storePerformance();
					next();
				},
				name: 'account-performance',
				component: () =>
					import(/* webpackChunkName: "account-performance" */ '@views/account/account-performance.vue')
			},
			{
				path: 'transactions',
				name: 'account-transactions',
				meta: { minTokenScopeRequired: 'FULL_ACCESS' },
				beforeEnter: async (to, from, next) => {
					investmentEntity.storeBankAccounts();
					next();
				},
				component: () =>
					import(/* webpackChunkName: "account-transactions" */ '@views/account/account-transactions.vue')
			}
		]
	},
	{
		path: `/account/overview`,
		name: 'account-overview',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.hasNeverBeenAnInvestor) {
				next({ name: 'user-overview' });
			} else if (getItemFromLocalStorage('redirectToWelcomeVideo')) {
				next({ name: 'welcome-video' });
			} else {
				investmentEntity.storePositions();
				investmentEntity.storePerformance();

				if (investmentEntity.showTaxCenter) {
					tax.storeTaxData();
				}
				next();
			}
		},
		component: () => import(/* webpackChunkName: "overview" */ '@src/views/account/account-overview.vue')
	},
	{
		path: `/account/user-dashboard/overview`,
		name: 'user-overview',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			if (!investmentEntity.hasNeverBeenAnInvestor) {
				next({ name: 'account-overview', replace: true });
			} else {
				if (!orderForm.dataRetrieved) {
					await orderForm.getUserCheckoutDetailsData();
					const notIraEntity =
						orderForm.signUpCheckoutType === 'IRA' && !investmentEntity.isIraEntity && user.hasIraEntity;
					if (notIraEntity) {
						await orderForm.submitSignupCheckoutType('TAXABLE');
					}
				}
				if (!questionnaire.dataRetrieved && !orderForm.plan) {
					await questionnaire.getFullResponse(true);
				}

				const promises = [investmentEntity.refreshPositionsAndPerformance()];
				if (orderForm.plan) {
					promises.push(investmentEntity.storeReitElementsForPlan(orderForm.plan.id));
				} else if (questionnaire.fullResponse.recommendedRiaPlan) {
					promises.push(
						investmentEntity.storeReitElementsForPlan(questionnaire.fullResponse.recommendedRiaPlan.id)
					);
				}

				if (
					investmentEntity.isIraEntity &&
					investmentEntity.iraEntityMetadata?.isMtcIra &&
					investmentEntity.iraEntityMetadata?.isIraOnboardingFinished
				) {
					promises.push(ira.storeIraFundingType());
				}

				await Promise.all(promises);

				next();
			}
		},
		component: () =>
			import(/* webpackChunkName: "user-overview" */ '@src/views/account/user-overview/user-overview.vue')
	},
	{
		path: '/account/newsfeed',
		name: 'newsfeed',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: (to, from, next) => {
			if (investmentEntity.hasNeverBeenAnInvestor) {
				next({ name: 'newsfeed-public' });
			} else {
				next();
			}
		},
		component: () => import(/* webpackChunkName: "newsfeed" */ '@views/account/newsfeed.vue')
	},
	{
		path: '/account/user-dashboard/newsfeed',
		name: 'newsfeed-public',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: (to, from, next) => {
			if (!investmentEntity.hasNeverBeenAnInvestor) {
				next({ name: 'newsfeed', replace: true });
			} else {
				next();
			}
		},
		component: () => import(/* webpackChunkName: "newsfeed-public" */ '@views/account/newsfeed-public.vue')
	},
	{
		path: '/account/performance/account-value-all-time',
		name: 'account-value-all-time',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			investmentEntity.storePerformance();
			next();
		},
		component: () =>
			import(
				/* webpackChunkName: "account-performance-all-time" */ '@views/account/account-performance-all-time.vue'
			)
	},
	{
		path: `/account/ineligible-for-drip`,
		name: 'ineligible-for-drip',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			if (investmentEntity.eligibleForDrip) {
				next({
					name: 'settings-drip-view'
				});
			} else {
				next();
			}
		},
		component: () =>
			import(/* webpackChunkName: "ineligible-for-drip" */ '@views/account/drip/ineligible-for-drip.vue')
	},
	{
		path: '/account/invest',
		name: 'account-invest',
		beforeEnter: async (to, from, next) => {
			const isIRA = investmentEntity.isIraEntity;
			const isEnabled = await app.isFeatureEnabled('invest-tab');

			if (isEnabled) {
				if (isIRA) {
					if (investmentEntity.iraEntityMetadata.isIraOnboardingFinished) {
						await next({ name: 'ira-add-funds-start', replace: true });
					} else {
						await next({ name: 'ira-start', replace: true });
					}
				} else {
					if (investmentEntity.additionalInvestmentEligibility === 'VALID') {
						if (addFunds.orderComplete) {
							addFunds.clearPreviousOrder();
						}

						try {
							if (!addFunds.investTabAddFundsMetadataLoaded) {
								await addFunds.fetchAndStoreInvestTabAddFundsMetadata();
							}

							if (investmentEntity.hasActiveRecurringInvestmentSchedule) {
								await investmentEntity.storeAutoInvestSchedule();
							} else {
								await Promise.all([
									investmentEntity.storeBankAccounts(),
									autoInvest.getFrequenciesAndAmounts(),
									autoInvest.getAgreements()
								]);
							}

							await next();
						} catch {
							await next({ name: 'account-overview', replace: true });
						}
					} else {
						await next({ name: 'add-funds-ria', replace: true });
					}
				}
			} else {
				await next({ name: 'account-overview', replace: true });
			}
		},
		component: () => import(/* webpackChunkName: "invest-tab" */ '@views/account/account-invest.vue')
	},
	{
		path: `/account/investment-accounts/backup-withholding/resolve`,
		name: 'backup-withholding-resolve',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () =>
			import(
				/* webpackChunkName: "backup-withholding-first-notice" */ '@views/backup-withholding/backup-withholding-first-notice.vue'
			)
	},
	{
		path: `/account/investment-accounts/backup-withholding/none-active`,
		name: 'backup-withholding-none-active',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () =>
			import(
				/* webpackChunkName: "backup-withholding-none-active" */ '@views/backup-withholding/backup-withholding-none-active.vue'
			)
	},
	{
		path: `/account/investment-accounts/backup-withholding/in-process`,
		name: 'backup-withholding-in-process',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () =>
			import(
				/* webpackChunkName: "backup-withholding-in-process" */ '@views/backup-withholding/backup-withholding-in-process.vue'
			)
	},
	{
		path: '/account/tax-center',
		name: 'tax-center',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			if (!investmentEntity.showTaxCenter) {
				next({ name: 'account-overview', replace: true });
			} else {
				await tax.storeTaxData();
				next();
			}
		},
		component: () => import(/* webpackChunkName: "account-tax-center" */ '@views/account/account-tax-center.vue')
	},
	{
		path: '/account/update-ssn/form',
		name: 'update-ssn-form',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			if (user.ssnNeedsUpdating) {
				next();
			} else {
				next({ name: 'update-ssn-save', replace: true });
			}
		},
		component: () => import(/* webpackChunkName: "update-ssn-form" */ '@views/account/update-ssn-form.vue')
	},
	{
		path: '/account/update-ssn/save',
		alias: `/account/update-ssn/save/:saveType`,
		name: 'update-ssn-save',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import(/* webpackChunkName: "update-ssn-save" */ '@views/account/update-ssn-save.vue')
	},
	{
		path: `/account-checklist`,
		name: 'account-checklist',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		component: () => import(/* webpackChunkName: "account-checklist" */ '@views/account/account-checklist.vue'),
		beforeEnter: async (to, from, next) => {
			removeItemFromSessionStorage('redirect-to-checklist');
			next();
		}
	},
	{
		path: `/account/security-questions/edit`,
		name: 'security-questions',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			if (user.user?.additionalAuthFactorsAnswered) {
				next({ name: 'account-overview', replace: true });
			} else {
				next();
			}
		},
		component: () =>
			import(/* webpackChunkName: "security-questions" */ '@views/account/security/security-questions.vue')
	},
	...accountSecurityRoutes,
	...addFundsRoutes,
	...autoInvestEnableRoutes,
	...riaUpgradeRoutes,
	...upgradeRoutes
];

export default accountRoutes;
